import { BOTS_HOST_URL } from '@/constants/bots';

/**
 * The site's own support agent (an Easy-Peasy.AI Bot), embedded on every
 * public page via `SiteChatWidget` — Chatbase-style "chat bubble on the
 * marketing site". Dashboard pages (Settings and Support included) hide it.
 */
export const SITE_CHAT_BOT_ID = '75ceddfd-387c-4612-af18-0e35b9de1c92';
export const SITE_CHAT_BOT_URL = `${BOTS_HOST_URL}/bot/${SITE_CHAT_BOT_ID}`;

/** Override to test a locally built launcher (e.g. `/chat.min.js`). */
export const SITE_CHAT_SCRIPT_URL =
  process.env.NEXT_PUBLIC_SITE_CHAT_SCRIPT_URL ||
  `${BOTS_HOST_URL}/chat.min.js`;

export const SITE_CHAT_LAUNCHER = {
  position: 'bottom-right',
  color: '#6366f1',
} as const;

export type SiteChatPlacement =
  /** Launcher visible. */
  | 'show'
  /** Launcher loaded but hidden (dashboard chrome); conversation state kept. */
  | 'hide'
  /**
   * Launcher must not exist at all — the page embeds ANOTHER chat widget with
   * the same DOM ids (`#dialoq`), or is itself a widget/preview surface.
   */
  | 'unmount';

// Matched against `router.pathname` — the ROUTE PATTERN (`/agents/[uuid]/preview`),
// which is locale-free and stable, not the rendered URL.
const UNMOUNT_ROUTES = [
  '/bot', // standalone bot page (the bots server's own chat UI)
  '/agents/[uuid]/preview', // rehearses a customer's chat.min.js on a placeholder site
  '/app-preview', // mobile WebView surface
  '/turnstile', // mobile login security widget
];

// App surfaces that do not go through DashboardLayout/NarrowDashboardLayout
// (or where a floating bubble would sit on top of the product's own composer),
// plus flows that should stay focused.
const HIDE_ROUTES = [
  // Members' account pages — listed explicitly (not left to the dashboard
  // shell) so the bubble stays off even on a full page load, and because
  // /support hands its corner to Crisp for human support.
  '/settings',
  '/support',
  '/chat',
  '/marky/website-builder',
  '/share', // shared Marky chats render the chat shell
  '/employees',
  '/agents',
  '/ai-video-editor',
  '/mcp/authorize',
  '/logout',
  '/activate',
  '/delete-account',
  '/cancel-subscription',
];

const matchesRoute = (pathname: string, route: string) =>
  pathname === route || pathname.startsWith(`${route}/`);

export function getSiteChatPlacement(
  pathname: string,
  { dashboardChromeMounted }: { dashboardChromeMounted: boolean },
): SiteChatPlacement {
  if (UNMOUNT_ROUTES.some((route) => matchesRoute(pathname, route))) {
    return 'unmount';
  }
  if (HIDE_ROUTES.some((route) => matchesRoute(pathname, route))) {
    return 'hide';
  }
  // Dual-mode pages (e.g. /create-video, /voice-changer, /tts) render the
  // marketing shell for visitors and the dashboard chrome for members; the
  // layouts report which one is mounted, so no route list is needed for them.
  return dashboardChromeMounted ? 'hide' : 'show';
}
